/**
 * WordPress dependencies
 */
import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import TimeInput from './TimeInput';
import {
	Card,
	CardHeader,
	CardBody,
	TextControl,
	SelectControl,
	TextareaControl,
	Button,
	Notice,
	Spinner,
} from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';

/**
 * Slot-based event types that use availability schedules and time slots
 */
const SLOT_BASED_TYPES = [ 'one-on-one', 'group' ];

/**
 * Edit Booking Form component
 *
 * @param {Object}   props
 * @param {number}   props.bookingId The booking ID to edit.
 * @param {Function} props.onSave    Callback when booking is saved.
 * @param {Function} props.onCancel  Callback when form is cancelled.
 * @return {JSX.Element} The EditBookingForm component.
 */
const EditBookingForm = ( { bookingId, onSave, onCancel } ) => {
	const [ formData, setFormData ] = useState( {
		attendee_name: '',
		attendee_email: '',
		attendee_phone: '',
		booking_date: '',
		booking_start: '',
		booking_end: '',
		booking_status: 'pending',
		booking_meta: {},
	} );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ isSaving, setIsSaving ] = useState( false );
	const [ notices, setNotices ] = useState( [] );
	const [ fieldErrors, setFieldErrors ] = useState( {} );

	// Event info loaded from booking response
	const [ eventInfo, setEventInfo ] = useState( null );

	// Slot-based booking state
	const [ isSlotBased, setIsSlotBased ] = useState( false );
	const [ bookableDates, setBookableDates ] = useState( [] );
	const [ availableSlots, setAvailableSlots ] = useState( [] );
	const [ loadingDates, setLoadingDates ] = useState( false );
	const [ loadingSlots, setLoadingSlots ] = useState( false );

	// Booking status options
	const statusOptions = [
		{ label: __( 'Pending', 'nemtly-booking' ), value: 'pending' },
		{ label: __( 'Confirmed', 'nemtly-booking' ), value: 'confirmed' },
		{ label: __( 'Cancelled', 'nemtly-booking' ), value: 'cancelled' },
	];

	// Load booking data
	useEffect( () => {
		if ( bookingId ) {
			loadBookingData();
		}
	}, [ bookingId ] );

	/**
	 * Load booking data from API, then fetch event details for slot-based handling
	 */
	const loadBookingData = async () => {
		try {
			setIsLoading( true );
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/bookings/${ bookingId }`,
			} );

			if ( response.success && response.result ) {
				const booking = response.result;

				// Extract time portion from datetime if needed (e.g., "2026-03-20 10:00:00" → "10:00")
				const startTime = extractTime( booking.booking_start );
				const endTime = extractTime( booking.booking_end );

				setFormData( {
					attendee_name: booking.attendee_name || '',
					attendee_email: booking.attendee_email || '',
					attendee_phone: booking.attendee_phone || '',
					booking_date: booking.booking_date || '',
					booking_start: startTime,
					booking_end: endTime,
					booking_status: booking.booking_status || 'pending',
					booking_meta: booking.booking_meta || {},
				} );

				setEventInfo( booking.event || null );

				// If event exists, fetch full event data to determine if slot-based
				if ( booking.event_id ) {
					await loadEventDetails(
						booking.event_id,
						booking.booking_date
					);
				}
			} else {
				throw new Error(
					response.message ||
						__( 'Failed to load booking data.', 'nemtly-booking' )
				);
			}
		} catch ( error ) {
			setNotices( [
				{
					type: 'error',
					message:
						error.message ||
						__( 'Failed to load booking data.', 'nemtly-booking' ),
				},
			] );
		} finally {
			setIsLoading( false );
		}
	};

	/**
	 * Load full event details to determine if slot-based
	 *
	 * @param {number} eventId     The event ID.
	 * @param {string} bookingDate The current booking date.
	 */
	const loadEventDetails = async ( eventId, bookingDate ) => {
		try {
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events/${ eventId }`,
			} );

			if ( response.success && response.result ) {
				const event = response.result;
				const eventIsSlotBased = SLOT_BASED_TYPES.includes(
					event.event_type
				);
				setIsSlotBased( eventIsSlotBased );

				if ( eventIsSlotBased ) {
					fetchBookableDates( eventId );
					// Also load slots for the current booking date
					if ( bookingDate ) {
						fetchAvailableSlots( eventId, bookingDate );
					}
				}
			}
		} catch ( error ) {
			// Non-critical — just means we can't determine slot-based status
		}
	};

	/**
	 * Fetch bookable dates for a slot-based event
	 *
	 * @param {number} eventId The event ID.
	 */
	const fetchBookableDates = async ( eventId ) => {
		setLoadingDates( true );
		try {
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events/${ eventId }/dates?limit=30`,
			} );
			if ( response.success && response.result ) {
				setBookableDates( response.result.dates || [] );
			}
		} catch ( error ) {
			// Non-critical
		} finally {
			setLoadingDates( false );
		}
	};

	/**
	 * Fetch available time slots for a specific date
	 *
	 * @param {number} eventId The event ID.
	 * @param {string} date    The date (Y-m-d).
	 */
	const fetchAvailableSlots = async ( eventId, date ) => {
		setLoadingSlots( true );
		setAvailableSlots( [] );
		try {
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events/${ eventId }/slots?date=${ date }`,
			} );
			if ( response.success && response.result ) {
				setAvailableSlots( response.result.slots || [] );
			}
		} catch ( error ) {
			// Non-critical
		} finally {
			setLoadingSlots( false );
		}
	};

	/**
	 * Extract time portion from a datetime or time string
	 *
	 * @param {string} value Datetime ("2026-03-20 10:00:00") or time ("10:00") string.
	 * @return {string} Time in H:i format, or empty string.
	 */
	const extractTime = ( value ) => {
		if ( ! value ) {
			return '';
		}
		// If it contains a space, it's a datetime — extract time
		if ( value.includes( ' ' ) ) {
			return value.split( ' ' )[ 1 ].substring( 0, 5 );
		}
		// Already a time string — return first 5 chars (H:i)
		return value.substring( 0, 5 );
	};

	/**
	 * Handle form input changes
	 *
	 * @param {string} field The field name.
	 * @param {*}      value The new value.
	 */
	const handleInputChange = ( field, value ) => {
		// Clear field error when user interacts
		if ( fieldErrors[ field ] ) {
			setFieldErrors( ( prev ) => {
				const updated = { ...prev };
				delete updated[ field ];
				return updated;
			} );
		}

		if ( field === 'booking_date' && isSlotBased ) {
			// When date changes for slot-based event, validate and fetch slots
			setFormData( ( prev ) => ( {
				...prev,
				booking_date: value,
				booking_start: '',
				booking_end: '',
			} ) );
			setAvailableSlots( [] );

			if ( value && eventInfo ) {
				if (
					bookableDates.length > 0 &&
					! bookableDates.includes( value )
				) {
					const dayName = new Date(
						value + 'T00:00:00'
					).toLocaleDateString( 'en-US', { weekday: 'long' } );
					setFieldErrors( ( prev ) => ( {
						...prev,
						booking_date: `${ dayName }s ${ __(
							'are not available for this event.',
							'nemtly-booking'
						) }`,
					} ) );
				} else {
					fetchAvailableSlots( eventInfo.id, value );
				}
			}
		} else {
			setFormData( ( prev ) => ( {
				...prev,
				[ field ]: value,
			} ) );
		}
	};

	/**
	 * Handle slot selection
	 *
	 * @param {Object} slot The selected slot object.
	 */
	const handleSlotSelect = ( slot ) => {
		if ( ! slot.available ) {
			return;
		}

		if ( fieldErrors.booking_start ) {
			setFieldErrors( ( prev ) => {
				const updated = { ...prev };
				delete updated.booking_start;
				return updated;
			} );
		}

		setFormData( ( prev ) => ( {
			...prev,
			booking_start: slot.start,
			booking_end: slot.end,
		} ) );
	};

	/**
	 * Validate form data
	 *
	 * @return {Object} Object mapping field names to error messages. Empty if valid.
	 */
	const validateForm = () => {
		const errors = {};

		if ( ! formData.attendee_name.trim() ) {
			errors.attendee_name = __(
				'Attendee name is required.',
				'nemtly-booking'
			);
		}

		if ( ! formData.attendee_email.trim() ) {
			errors.attendee_email = __(
				'Attendee email is required.',
				'nemtly-booking'
			);
		} else if ( ! isValidEmail( formData.attendee_email ) ) {
			errors.attendee_email = __(
				'Please enter a valid email address.',
				'nemtly-booking'
			);
		}

		if ( ! formData.booking_date ) {
			errors.booking_date = __(
				'Booking date is required.',
				'nemtly-booking'
			);
		}

		// Validate slot selection for slot-based events
		if ( isSlotBased && ! formData.booking_start ) {
			errors.booking_start = __(
				'Please select a time slot.',
				'nemtly-booking'
			);
		}

		return errors;
	};

	/**
	 * Validate email format
	 *
	 * @param {string} email Email to validate.
	 * @return {boolean} True if email is valid.
	 */
	const isValidEmail = ( email ) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test( email );
	};

	/**
	 * Handle form submission
	 */
	const handleSubmit = async () => {
		const errors = validateForm();
		if ( Object.keys( errors ).length > 0 ) {
			setFieldErrors( errors );
			return;
		}

		setFieldErrors( {} );

		try {
			setIsSaving( true );
			setNotices( [] );

			const response = await apiFetch( {
				path: `/nemtly-booking/v1/bookings/${ bookingId }`,
				method: 'PUT',
				data: formData,
			} );

			if ( response.success ) {
				setNotices( [
					{
						type: 'success',
						message: __(
							'Booking updated successfully.',
							'nemtly-booking'
						),
					},
				] );

				if ( onSave ) {
					onSave( response.result );
				}
			} else {
				throw new Error(
					response.message ||
						__( 'Failed to update booking.', 'nemtly-booking' )
				);
			}
		} catch ( error ) {
			setNotices( [
				{
					type: 'error',
					message:
						error.message ||
						__( 'Failed to update booking.', 'nemtly-booking' ),
				},
			] );
		} finally {
			setIsSaving( false );
		}
	};

	/**
	 * Format time for display
	 *
	 * @param {string} timeString The time string to format (HH:mm).
	 * @return {string} Formatted time string (e.g., 10:00 AM).
	 */
	const formatDisplayTime = ( timeString ) => {
		if ( ! timeString ) {
			return '';
		}
		const [ hours, minutes ] = timeString.split( ':' );
		const date = new Date();
		date.setHours( hours );
		date.setMinutes( minutes );
		return date.toLocaleTimeString( 'en-US', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		} );
	};

	/**
	 * Format date for display label
	 *
	 * @param {string} dateString The date string (Y-m-d).
	 * @return {string} Formatted label.
	 */
	const formatDateLabel = ( dateString ) => {
		if ( ! dateString ) {
			return '';
		}
		const date = new Date( dateString + 'T00:00:00' );
		return date.toLocaleDateString( 'en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		} );
	};

	if ( isLoading ) {
		return (
			<div className="nb-text-center nb-p-8">
				<Spinner />
				<p>{ __( 'Loading booking data…', 'nemtly-booking' ) }</p>
			</div>
		);
	}

	return (
		<div className="nb-edit-booking-form nb-max-w-4xl nb-mx-auto nb-p-6">
			{ notices.map( ( notice, index ) => (
				<Notice
					key={ index }
					status={ notice.type }
					onRemove={ () =>
						setNotices( ( prev ) =>
							prev.filter( ( _, i ) => i !== index )
						)
					}
				>
					{ notice.message }
				</Notice>
			) ) }

			<Card>
				<CardHeader>
					<h2>{ __( 'Edit Booking', 'nemtly-booking' ) }</h2>
				</CardHeader>
				<CardBody>
					{ /* Event info display */ }
					{ eventInfo && (
						<div className="nb-form-row nb-bg-gray-50 nb-p-4 nb-rounded-lg nb-border nb-border-gray-200">
							<div className="nb-text-sm nb-text-gray-700">
								<strong>
									{ __( 'Event', 'nemtly-booking' ) }
								</strong>
								{ ': ' }
								{ eventInfo.title ||
									`Event #${ eventInfo.id }` }
							</div>
						</div>
					) }

					<div
						className={ `nb-form-row ${
							fieldErrors.attendee_name ? 'nb-field-error' : ''
						}` }
					>
						<TextControl
							label={ __( 'Attendee Name', 'nemtly-booking' ) }
							value={ formData.attendee_name }
							onChange={ ( value ) =>
								handleInputChange( 'attendee_name', value )
							}
							help={ fieldErrors.attendee_name || '' }
							className={
								fieldErrors.attendee_name ? 'nb-has-error' : ''
							}
							required
						/>
					</div>

					<div
						className={ `nb-form-row ${
							fieldErrors.attendee_email ? 'nb-field-error' : ''
						}` }
					>
						<TextControl
							label={ __( 'Attendee Email', 'nemtly-booking' ) }
							type="email"
							value={ formData.attendee_email }
							onChange={ ( value ) =>
								handleInputChange( 'attendee_email', value )
							}
							help={ fieldErrors.attendee_email || '' }
							className={
								fieldErrors.attendee_email ? 'nb-has-error' : ''
							}
							required
						/>
					</div>

					<div className="nb-form-row">
						<TextControl
							label={ __( 'Attendee Phone', 'nemtly-booking' ) }
							value={ formData.attendee_phone }
							onChange={ ( value ) =>
								handleInputChange( 'attendee_phone', value )
							}
						/>
					</div>

					{ /* Date selection — slot-based uses datepicker with validation, date-based uses plain input */ }
					{ isSlotBased ? (
						<div
							className={ `nb-form-row ${
								fieldErrors.booking_date ? 'nb-field-error' : ''
							}` }
						>
							{ loadingDates ? (
								<div className="nb-flex nb-items-center nb-gap-2 nb-py-2">
									<Spinner />
									<span className="nb-text-sm nb-text-gray-500">
										{ __(
											'Loading available dates…',
											'nemtly-booking'
										) }
									</span>
								</div>
							) : (
								<>
									<TextControl
										label={ __(
											'Booking Date',
											'nemtly-booking'
										) }
										type="date"
										value={ formData.booking_date }
										onChange={ ( value ) =>
											handleInputChange(
												'booking_date',
												value
											)
										}
										help={ fieldErrors.booking_date || '' }
										className={
											fieldErrors.booking_date
												? 'nb-has-error'
												: ''
										}
										required
									/>
									{ bookableDates.length > 0 && (
										<div className="nb-mt-1 nb-text-xs nb-text-gray-500">
											{ __(
												'Available dates:',
												'nemtly-booking'
											) }{ ' ' }
											{ bookableDates
												.slice( 0, 5 )
												.map( ( d ) =>
													formatDateLabel( d )
												)
												.join( ', ' ) }
											{ bookableDates.length > 5 &&
												` ${ __(
													'and',
													'nemtly-booking'
												) } ${
													bookableDates.length - 5
												} ${ __(
													'more',
													'nemtly-booking'
												) }` }
										</div>
									) }
								</>
							) }
						</div>
					) : (
						<div
							className={ `nb-form-row ${
								fieldErrors.booking_date ? 'nb-field-error' : ''
							}` }
						>
							<TextControl
								label={ __( 'Booking Date', 'nemtly-booking' ) }
								type="date"
								value={ formData.booking_date }
								onChange={ ( value ) =>
									handleInputChange( 'booking_date', value )
								}
								help={ fieldErrors.booking_date || '' }
								className={
									fieldErrors.booking_date
										? 'nb-has-error'
										: ''
								}
								required
							/>
						</div>
					) }

					{ /* Slot picker for slot-based events */ }
					{ isSlotBased && formData.booking_date && (
						<div
							className={ `nb-form-row ${
								fieldErrors.booking_start
									? 'nb-field-error'
									: ''
							}` }
						>
							<label className="nb-block nb-text-sm nb-font-medium nb-text-gray-700 nb-mb-2">
								{ __( 'Time Slot', 'nemtly-booking' ) }
							</label>
							{ loadingSlots ? (
								<div className="nb-flex nb-items-center nb-gap-2 nb-py-2">
									<Spinner />
									<span className="nb-text-sm nb-text-gray-500">
										{ __(
											'Loading time slots…',
											'nemtly-booking'
										) }
									</span>
								</div>
							) : availableSlots.length > 0 ? (
								<>
									<div className="nb-slot-picker-grid">
										{ availableSlots.map( ( slot ) => {
											const isSelected =
												formData.booking_start ===
												slot.start;
											const isFull =
												! slot.available &&
												! isSelected;

											let slotClass = 'nb-slot-btn';
											if ( isFull ) {
												slotClass +=
													' nb-slot-btn--full';
											} else if ( isSelected ) {
												slotClass +=
													' nb-slot-btn--selected';
											}

											return (
												<button
													key={ slot.start }
													type="button"
													className={ slotClass }
													onClick={ () =>
														handleSlotSelect( slot )
													}
													disabled={ isFull }
												>
													<div className="nb-slot-btn__time">
														{ formatDisplayTime(
															slot.start
														) }{ ' ' }
														–{ ' ' }
														{ formatDisplayTime(
															slot.end
														) }
													</div>
													<div className="nb-slot-btn__info">
														{ isFull
															? __(
																	'Full',
																	'nemtly-booking'
															  )
															: slot.total_capacity ===
															  1
															? __(
																	'Available',
																	'nemtly-booking'
															  )
															: slot.spots_remaining >=
															  999
															? __(
																	'Available',
																	'nemtly-booking'
															  )
															: slot.spots_remaining ===
															  1
															? __(
																	'1 spot left',
																	'nemtly-booking'
															  )
															: `${
																	slot.spots_remaining
															  } ${ __(
																	'spots',
																	'nemtly-booking'
															  ) }` }
													</div>
												</button>
											);
										} ) }
									</div>
									{ fieldErrors.booking_start && (
										<p className="nb-text-sm nb-text-red-600 nb-mt-1">
											{ fieldErrors.booking_start }
										</p>
									) }
								</>
							) : (
								<p className="nb-text-sm nb-text-gray-500">
									{ __(
										'No time slots available for this date.',
										'nemtly-booking'
									) }
								</p>
							) }
						</div>
					) }

					{ /* Manual time inputs — only for non-slot-based events */ }
					{ ! isSlotBased && (
						<div className="nb-form-row nb-form-columns nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-4">
							<TimeInput
								label={ __( 'Start Time', 'nemtly-booking' ) }
								value={ formData.booking_start }
								onChange={ ( value ) =>
									handleInputChange( 'booking_start', value )
								}
							/>
							<TimeInput
								label={ __( 'End Time', 'nemtly-booking' ) }
								value={ formData.booking_end }
								onChange={ ( value ) =>
									handleInputChange( 'booking_end', value )
								}
							/>
						</div>
					) }

					<div className="nb-form-row">
						<SelectControl
							label={ __( 'Status', 'nemtly-booking' ) }
							value={ formData.booking_status }
							options={ statusOptions }
							onChange={ ( value ) =>
								handleInputChange( 'booking_status', value )
							}
						/>
					</div>

					<div className="nb-form-row">
						<TextareaControl
							label={ __( 'Additional Notes', 'nemtly-booking' ) }
							value={ formData.booking_meta?.notes || '' }
							onChange={ ( value ) =>
								handleInputChange( 'booking_meta', {
									...formData.booking_meta,
									notes: value,
								} )
							}
							rows={ 4 }
						/>
					</div>

					<div className="nb-form-actions">
						<Button
							isPrimary
							onClick={ handleSubmit }
							isBusy={ isSaving }
							disabled={ isSaving }
						>
							{ isSaving
								? __( 'Saving…', 'nemtly-booking' )
								: __( 'Update Booking', 'nemtly-booking' ) }
						</Button>
						{ onCancel && (
							<Button
								isSecondary
								onClick={ onCancel }
								disabled={ isSaving }
							>
								{ __( 'Cancel', 'nemtly-booking' ) }
							</Button>
						) }
					</div>
				</CardBody>
			</Card>
		</div>
	);
};

export default EditBookingForm;
